import { Image, Text } from '@capsicum/common-web';
import { MarketingBannerThemes } from '@capsicum/features/feeds/shared';
import { type AriaAttributes, type AriaRole, type FC, type PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

export type WrapperProps = PropsWithChildren &
	AriaAttributes & {
		$backgroundImage?: string;
		$clickable: boolean;
		$height?: number;
		onClick?: () => void;
		role?: AriaRole;
	};

export const Wrapper = styled.div<WrapperProps>`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	${({ theme }) => theme.border.radius.medium};
	gap: ${({ theme }) => theme.spacings.xxxsmall};
	padding: ${({ theme }) => theme.spacings.small};
	cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'auto')};

	${({ $height }) =>
		$height &&
		css`
			height: ${$height}px;
		`}
`;

export const StyledText = styled(Text)<{ $color: string }>`
	color: ${({ $color }) => $color};
`;

export const BannerImage = styled(Image)<{ $verticalObjectPosition?: string }>`
	width: 100%;
	max-height: 100%;
	object-fit: contain;
	object-position: 50% ${({ $verticalObjectPosition }) => $verticalObjectPosition ?? 0};
`;

const SociosGradient =
	'linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 46, 60, 0.6) 100%), linear-gradient(83deg, #002e3c 0.84%, rgba(5, 83, 241, 0.6) 51.44%, #002137 99.74%), #002e3c';

const SociosLightGradient =
	'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 46, 60, 0.6) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), #0553f1';

const ChilizGradient =
	'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 46, 60, 0.6) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), linear-gradient(83deg, rgba(47, 5, 186, 0.5) 0.84%, rgba(255, 18, 86, 0.6) 99.74%), #2f05ba';

const ChilizInverseGradient =
	'linear-gradient(83deg, rgba(0, 0, 0, 0) 0.58%, rgba(0, 46, 60, 0.6) 99.46%), linear-gradient(262deg, rgba(47, 5, 186, 0.4) 0%, rgba(255, 18, 86, 0.8) 100%), #023a94';

const PepperGradient =
	'linear-gradient(71.27deg, rgba(254, 208, 102, 0.6) 45.37%, rgba(220, 48, 53, 0.6) 99.42%), linear-gradient(0deg, #fed066, #fed066)';

const SociosWrapper = styled(Wrapper)`
	background: ${SociosGradient};
`;

export const SociosLightWrapper = styled(Wrapper)`
	background: ${SociosLightGradient};
`;

export const ChilizWrapper = styled(Wrapper)`
	background: ${ChilizGradient};
`;

export const ChilizInverseWrapper = styled(Wrapper)`
	background: ${ChilizInverseGradient};
`;

export const PepperWrapper = styled(Wrapper)`
	background: ${PepperGradient};
`;

export const CustomWrapper = styled(Wrapper)<{ $backgroundImage?: string }>`
	background-image: url('${({ $backgroundImage }) => $backgroundImage}');
	min-height: 158px;
`;

export const MARKETING_BANNER_WRAPPER_MAP: Record<MarketingBannerThemes, FC<WrapperProps>> = {
	[MarketingBannerThemes.CHILIZ]: ChilizWrapper,
	[MarketingBannerThemes.CHILIZ_INVERSE]: ChilizInverseWrapper,
	[MarketingBannerThemes.CUSTOM]: CustomWrapper,
	[MarketingBannerThemes.PEPPER]: PepperWrapper,
	[MarketingBannerThemes.SOCIOS]: SociosWrapper,
	[MarketingBannerThemes.SOCIOS_LIGHT]: SociosLightWrapper
};

export const MARKETING_BANNER_GRADIENT_MAP: Record<MarketingBannerThemes, string> = {
	[MarketingBannerThemes.CHILIZ]: ChilizGradient,
	[MarketingBannerThemes.CHILIZ_INVERSE]: ChilizInverseGradient,
	[MarketingBannerThemes.CUSTOM]: SociosGradient,
	[MarketingBannerThemes.PEPPER]: PepperGradient,
	[MarketingBannerThemes.SOCIOS]: SociosGradient,
	[MarketingBannerThemes.SOCIOS_LIGHT]: SociosLightGradient
};
